// Taking a whole round back.
//
// `GameStore.undo` takes back one action of the turn you are standing in and
// stops at its start (§29 U2). The Time Fluxxer (was the Time Fluxxer, §31 C11) is
// the other case and is defined against it: it rewinds a whole *round* — to
// the start of your turn in the round before this one (`Position.round`), with
// every army's turn since — and what pays for that is that the card is then gone.
//
// **A rewind is a log operation, so it cannot be a card effect.** `Card.apply`
// is handed a cloned `GameState` and the reducer never sees the log — the same
// split that keeps undo out of `src/engine/rules/` altogether. So the card's reducer
// half does nothing at all, and the whole of it lives here, beside the store
// that owns the log.
//
// **Truncating removes the play, which is the paradox and also its own fix.**
// The command that played the card sits *after* the commands being cut, so a
// plain truncate hands the card back with the board and the rewind is free for
// ever. The log therefore ends with a `burn-card`: the card was spent, at a
// moment that no longer exists, and "spent" is the half that is still true.
// That keeps the log what it is — replay it from the base and you get exactly
// the board on screen.
//
// **And the trailing `burn-card` does not, on its own, stop the card paying for
// itself twice** — which is the same paradox one step further out. The board a
// rewind lands on is *earlier* than the burn that spent the card, so the card is
// still in the deck there for ever: ask only that board and a second rewind a
// round later is granted, truncates back past the burn, and burns the card
// again. So ownership is asked of **both** boards — the one you are on and the
// one you land on — and only the first of those can tell a card that has been
// spent from one that had not arrived yet. The interface never offers a spent
// card (`playCard` looks it up in the deck first), so nothing reached this; a
// guard that exists only in the caller is one the next caller does not have.
//
// **`burn-card` rather than re-playing the card, because "your hand" is a
// different question at the rewind point.** `dealHand` samples the deck once
// the deck passes `RULES.handSize`, so a card in your hand now need not have
// been in it two turns ago — and `play-card` checks the hand, correctly. What
// survives a rewind is ownership, not the deal.

import type { Side } from "src/engine/rules/board";
import type { Command, GameState } from "src/engine/rules/commands";
import { lastIndexOf, walkTurns } from "src/engine/rules/turns";

/**
 * Why a rewind cannot happen. Each names something the player can act on,
 * except the last, which names a bug and says so out loud rather than letting
 * the card be spent on a board that did not move.
 */
export type RewindRefusal =
  | "card-not-held"
  | "no-turn-to-take-back"
  | "not-yet-earned"
  | "rewind-refused";

/**
 * Where a rewind lands, or why it does not happen.
 *
 * `upto` is the last log index kept, so the store replays `log.slice(0, upto +
 * 1)`. **-1 is a real answer and not a failure**: it is the base state, which
 * is where a continued run starts from, since `restore` lands a snapshot with
 * an empty log.
 */
export type RewindPlan = { ok: true; upto: number } | { ok: false; why: RewindRefusal };

/**
 * Where the player's previous turn began.
 *
 * **A turn boundary is the side to move changing, never a list of command
 * types**, and the walk that finds them is `rules/turns.ts` — shared with Undo,
 * which floors at the *last* of those boundaries where this lands on the one
 * before it. Two things come out right for free: Industrial Titan (several
 * moves inside one turn, one boundary) and a run with several camps (one
 * rewind takes back your turn *and* every army's turn since it).
 *
 * **`new-game` is the floor, for `undoFloor`'s reason.** It is not a move — it
 * is the command that made the board, the level, the deck and the seed — so
 * crossing it does not return you to an earlier position, it returns you to a
 * different game. Measured there before that floor existed: two clicks of undo
 * on turn one of a run left the classic sixty-four squares, thirty-two pieces
 * and no run at all.
 *
 * The card has to survive the trip, and it is asked about **twice**, which is
 * two different questions rather than one asked carefully. On the board you are
 * standing on it says the card is yours to spend — the half that catches one
 * already burnt, since a rewind lands *before* the burn that spent it and every
 * later look at that board finds it in the deck for ever. On the board you land
 * on it says the card had arrived by then: an artifact claimed during the turn
 * being taken back is one you had not earned at the point you would arrive at,
 * and burning it there would be burning something that does not exist.
 *
 * One replay of the log, so it is a click's worth of work and not a frame's.
 * Callers ask it when the card is played, never per render.
 */
export function planRewind(
  base: GameState,
  log: readonly Command[],
  side: Side,
  instance: number
): RewindPlan {
  // The walk itself is `src/engine/rules/turns.ts` — Undo asks the same question of
  // the same log, and U3's server asks it of the room's, so there is one of it.
  // Every point at which it became this side's move: the last is the turn being
  // played right now; the one before it is where the card goes back to.
  const { starts, state } = walkTurns(base, log, side, lastIndexOf(log, "new-game"));

  // `state` is the board being played on, the log having been replayed
  // whole. Asked before the turn count, because "you no longer have this card"
  // outranks anything about where a rewind would land — and after a rewind both
  // are true at once, the board being back at the start of your own turn.
  if (!held(state, instance)) return { ok: false, why: "card-not-held" };

  const target = previousRound(starts);
  if (!target) return { ok: false, why: "no-turn-to-take-back" };
  if (!held(target.state, instance)) return { ok: false, why: "not-yet-earned" };
  return { ok: true, upto: target.at };
}

/**
 * **The previous round, by `Position.round`** (§31 C11): the latest of your
 * turn starts in an earlier round than the turn being played. The same board
 * as "the turn before" on every ordinary clock, and the one a skipped turn or
 * a re-entered round cannot confuse.
 */
function previousRound<T extends { state: GameState }>(starts: readonly T[]): T | undefined {
  if (starts.length < 2) return undefined;
  const now = starts[starts.length - 1].state.position.round;
  for (let i = starts.length - 2; i >= 0; i--) {
    if (starts[i].state.position.round < now) return starts[i];
  }
  return undefined;
}

/**
 * Training's Undo past End Turn (§31 G1, `ModeConfig.undoPast`): the
 * Fluxxer's walk with no card — the last log index kept, or null when there
 * is no earlier round of yours since `new-game`.
 */
export function planRoundBack(base: GameState, log: readonly Command[], side: Side): number | null {
  const { starts } = walkTurns(base, log, side, lastIndexOf(log, "new-game"));
  return previousRound(starts)?.at ?? null;
}

/** Whether the run's deck holds that card instance on the given board. */
function held(state: GameState, instance: number): boolean {
  return state.run?.deck.some((entry) => entry.id === instance) ?? false;
}
